import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import { ReactElement, useMemo, useState } from "react";
import * as Yup from "yup";

import {
  CampaignContactFollowUpImportArgs,
  CampaignListResult,
  FollowUpFilterInput,
  ListTagsQuery,
  ListTagsQueryVariables,
  TagSortBy,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import Loading from "~frontend/components/Loading";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import TooltipButton from "~frontend/components/TooltipButton";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikSubmitButton } from "~frontend/components/forms/SubmitButton";
import CheckBoxUnchecked from "~frontend/components/icons/CheckBoxUnchecked";
import { LIST_TAGS } from "~frontend/graphql/tags";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";

import {
  FOLLOW_UP_FILTER_LABELS,
  FOLLOW_UP_FILTERS,
} from "../../campaignEditorUtils/FollowUpCampaignFilterSummary";
import FollowUpCampaignSelector from "./FollowUpCampaignSelector";

export default function ImportForm({
  onSubmit,
  initialValues,
  initialCampaignData,
  readOnly,
  resetContactSourceSettings,
  campaignIntegrationFilters,
}: {
  onSubmit?: (values: any) => void;
  initialValues: CampaignContactFollowUpImportArgs;
  initialCampaignData: CampaignListResult[];
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
  campaignIntegrationFilters: Record<string, any>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [selectedCampaigns, setSelectedCampaigns] =
    useState<CampaignListResult[]>(initialCampaignData);

  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const listTagsQuery = useQuery<ListTagsQuery, ListTagsQueryVariables>(
    LIST_TAGS,
    {
      variables: {
        input: {
          sort: TagSortBy.NameAsc,
          subaccountId: subaccount.id,
        },
      },
    }
  );

  const deletedTags = initialValues.followUpTagIds?.filter(
    id =>
      listTagsQuery.data &&
      !listTagsQuery.data.listTags.tags.find(tag => tag.id === id)
  );

  const scriptVariantOptions = useMemo(() => {
    const options: {
      [scriptVariantId: string]: {
        campaign: CampaignListResult;
        scriptVariant: CampaignListResult["scriptVariants"][number];
        indexInCampaign: number;
      };
    } = {};
    for (const campaign of selectedCampaigns) {
      let indexInCampaign = 0;
      for (const scriptVariant of _.sortBy(campaign.scriptVariants, [
        variant => (variant.deletedAt ? 1 : 0),
        variant => variant.order,
      ])) {
        options[scriptVariant.id] = {
          campaign,
          scriptVariant,
          indexInCampaign,
        };
        indexInCampaign++;
      }
    }

    return options;
  }, [selectedCampaigns]);

  const filterButtonText = (values: CampaignContactFollowUpImportArgs) => {
    if (values.followUpFilter === FollowUpFilterInput.All) {
      return "Filter Selected";
    }

    let filterCount: number;
    if (values.followUpFilter === FollowUpFilterInput.Tagged) {
      filterCount = values.followUpTagIds?.length || 1;
    } else if (values.followUpFilter === FollowUpFilterInput.ScriptVariant) {
      filterCount = values.followUpScriptVariantIds?.length || 1;
    } else {
      filterCount = 1;
    }

    return `Filter Selected (${filterCount})`;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit || _.noop}
      validationSchema={Yup.object().shape({
        followUpFilter: Yup.string().required(),
        followUpTagIds: Yup.array()
          .of(Yup.string())
          .when("followUpFilter", {
            is: FollowUpFilterInput.Tagged,
            then: Yup.array()
              .of(Yup.string())
              .required()
              .min(1, "Must select at least one tag")
              .test(
                "no-deleted-tags",
                "Cannot filter on a deleted tag",
                tagIds => {
                  if (!listTagsQuery.data || !tagIds) {
                    return true;
                  }
                  const nonDeletedTags = new Set(
                    listTagsQuery.data.listTags.tags.map(tag => tag.id)
                  );
                  return tagIds?.every(id => id && nonDeletedTags.has(id));
                }
              ),
          }),
        followUpScriptVariantIds: Yup.array()
          .of(Yup.string())
          .when("followUpFilter", {
            is: FollowUpFilterInput.ScriptVariant,
            then: Yup.array()
              .of(Yup.string())
              .required()
              .min(1, "Must select at least one initial variant"),
          }),
      })}
    >
      {({
        dirty,
        values,
        setFieldValue,
        resetForm,
        submitForm,
        isSubmitting,
      }) => {
        const campaignIdsWithSelectedScriptVariantIds = new Set(
          values.followUpScriptVariantIds
            ?.map(
              scriptVariantId =>
                scriptVariantOptions[scriptVariantId]?.campaign.id
            )
            .filter(campaignId => !!campaignId)
        );
        const allSelectedCampaignsHaveSelectedScriptVariants =
          selectedCampaigns.every(campaign =>
            campaignIdsWithSelectedScriptVariantIds.has(campaign.id)
          );

        return (
          <>
            <RouteLeavingGuard when={dirty} />

            <Form>
              <Field type="hidden" name="followUpCampaignIds" />
              <FollowUpCampaignSelector
                selectedCampaigns={selectedCampaigns}
                setSelectedCampaigns={setSelectedCampaigns}
                readOnly={readOnly}
                currentCampaignId={initialValues.campaignId}
                campaignIntegrationFilters={campaignIntegrationFilters}
                footerActions={
                  onSubmit && (
                    <FlexBox sx={{ justifyContent: "flex-end" }}>
                      {resetContactSourceSettings && dirty ? (
                        <Button
                          sx={{
                            mx: 2,
                            fontSize: "10px",
                            padding: "6px 8px",
                            height: "auto",
                            marginRight: "8px",
                            marginLeft: 0,
                          }}
                          variant="contained"
                          color="tertiary"
                          onClick={() => {
                            resetForm();
                            resetContactSourceSettings();
                          }}
                        >
                          Discard Changes
                        </Button>
                      ) : null}
                      <FormikSubmitButton
                        disabled={readOnly || !selectedCampaigns.length}
                        tooltipText={
                          selectedCampaigns.length === 0
                            ? "Select at least one campaign to import contacts from"
                            : undefined
                        }
                        submitText={
                          resetContactSourceSettings
                            ? "Reload Contacts"
                            : "Load Contacts"
                        }
                        loadingText={
                          resetContactSourceSettings
                            ? "Reloading Contacts..."
                            : "Loading Contacts..."
                        }
                        onClick={async () => {
                          await setFieldValue(
                            "followUpFilter",
                            FollowUpFilterInput.All
                          );
                          await setFieldValue("followUpTagIds", []);
                          await setFieldValue("followUpScriptVariantIds", []);
                          await submitForm().catch(console.error);
                        }}
                        sx={{
                          fontSize: "10px",
                          padding: "6px 8px",
                          height: "auto",
                        }}
                      />
                      <TooltipButton
                        disabled={
                          readOnly || !selectedCampaigns.length || isSubmitting
                        }
                        tooltipText={
                          selectedCampaigns.length === 0
                            ? "Select at least one campaign to import contacts from"
                            : undefined
                        }
                        onClick={() => setFilterDialogOpen(true)}
                        variant="contained"
                        color="primary"
                        sx={{
                          fontSize: "10px",
                          padding: "6px 8px",
                          height: "auto",
                          marginLeft: "8px",
                        }}
                      >
                        {filterButtonText(values)}
                      </TooltipButton>
                    </FlexBox>
                  )
                }
              />

              <Dialog open={filterDialogOpen}>
                <DialogTitle sx={{ paddingTop: "24px", paddingBottom: "8px" }}>
                  Filter Contacts to Load From {selectedCampaigns.length}{" "}
                  Campaigns
                </DialogTitle>
                <DialogContent>
                  <Typography sx={{ paddingBottom: "24px" }}>
                    Apply filters to target a selection of contacts from these
                    campaigns.
                  </Typography>
                  <FormikSelect
                    label="Filter criteria"
                    name="followUpFilter"
                    withNoneOption={false}
                    fullWidth
                    sx={{ marginBottom: theme.spacing(2) }}
                    disabled={readOnly}
                  >
                    {FOLLOW_UP_FILTERS.map(k => (
                      <MenuItem value={k} key={k}>
                        {FOLLOW_UP_FILTER_LABELS[k]}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                  {values.followUpFilter === FollowUpFilterInput.Tagged && (
                    <Box sx={{ paddingLeft: "40px" }}>
                      <FormikSelect
                        label="Tags"
                        name="followUpTagIds"
                        fullWidth
                        disabled={readOnly}
                        overrideFormikProps={field => ({
                          ...field,
                          multiple: true,
                        })}
                        renderValue={() => {
                          if (!listTagsQuery.data) {
                            return "Loading...";
                          }

                          return values.followUpTagIds
                            ?.map(
                              id =>
                                listTagsQuery.data!.listTags.tags.find(
                                  tag => tag.id === id
                                )?.name ?? "(deleted tag)"
                            )
                            .join(", ");
                        }}
                      >
                        {listTagsQuery.data ? (
                          [
                            ...listTagsQuery.data.listTags.tags.map(tag => (
                              <MenuItem value={tag.id} key={tag.id}>
                                <Checkbox
                                  checked={values.followUpTagIds?.includes(
                                    tag.id
                                  )}
                                  icon={<CheckBoxUnchecked />}
                                />
                                {tag.name}
                              </MenuItem>
                            )),
                            ...(deletedTags?.map(id => (
                              <MenuItem value={id} key={id}>
                                <Checkbox
                                  checked={values.followUpTagIds?.includes(id)}
                                  icon={<CheckBoxUnchecked />}
                                />{" "}
                                (deleted tag)
                              </MenuItem>
                            )) ?? []),
                          ]
                        ) : (
                          <MenuItem disabled>
                            <Loading />
                          </MenuItem>
                        )}
                      </FormikSelect>
                    </Box>
                  )}
                  {values.followUpFilter ===
                    FollowUpFilterInput.ScriptVariant && (
                    <Box sx={{ paddingLeft: "40px" }}>
                      <FormikSelect
                        label="Initial Variant"
                        name="followUpScriptVariantIds"
                        fullWidth
                        disabled={readOnly}
                        overrideFormikProps={field => ({
                          ...field,
                          multiple: true,
                        })}
                        renderValue={() =>
                          values.followUpScriptVariantIds
                            ?.map(id => {
                              const option = scriptVariantOptions[id];

                              if (option.scriptVariant.deletedAt) {
                                return `${option.campaign.name} - Deleted Variant - ${option.scriptVariant.script.slice(0, 50)}...`;
                              }

                              return `${option.campaign.name} - Variant ${option.indexInCampaign + 1} - ${option.scriptVariant.script.slice(0, 50)}...`;
                            })
                            .join(", ")
                        }
                      >
                        {Object.keys(scriptVariantOptions).map(
                          scriptVariantId => {
                            const option =
                              scriptVariantOptions[scriptVariantId];
                            return (
                              <MenuItem
                                value={scriptVariantId}
                                key={scriptVariantId}
                              >
                                <Checkbox
                                  checked={values.followUpScriptVariantIds?.includes(
                                    scriptVariantId
                                  )}
                                  icon={<CheckBoxUnchecked />}
                                />
                                <Box>
                                  <Typography
                                    variant="body3"
                                    sx={{ color: palette.darkGray }}
                                    component="p"
                                  >
                                    {option?.campaign.name}
                                  </Typography>
                                  {option.scriptVariant.deletedAt
                                    ? `${option.campaign.name} - Deleted Variant - ${option.scriptVariant.script.slice(0, 70)}...`
                                    : `${option.campaign.name} - Variant ${option.indexInCampaign + 1} - ${option.scriptVariant.script.slice(0, 70)}...`}
                                </Box>
                              </MenuItem>
                            );
                          }
                        )}
                      </FormikSelect>
                      {!allSelectedCampaignsHaveSelectedScriptVariants &&
                        !!values.followUpScriptVariantIds?.length && (
                          <Alert severity="warning">
                            <strong>Warning:</strong> You have only filtered for
                            initial variants from{" "}
                            {campaignIdsWithSelectedScriptVariantIds.size} of
                            the {selectedCampaigns.length} campaigns you have
                            selected.
                          </Alert>
                        )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions
                  sx={{ paddingRight: "24px", paddingBottom: "24px" }}
                >
                  <Button
                    onClick={() => setFilterDialogOpen(false)}
                    color="tertiary"
                    variant="contained"
                  >
                    Cancel
                  </Button>
                  <FormikSubmitButton onClick={submitForm}>
                    Load Contacts
                  </FormikSubmitButton>
                </DialogActions>
              </Dialog>
            </Form>
          </>
        );
      }}
    </Formik>
  );
}
